import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, api } from '../context/AuthContext';
import { format } from 'date-fns';
import { Clock, Check, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { BookingsList } from '../components/consulting/BookingsList';

export default function ConsultingPage() {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [myBookings, setMyBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    topic: '',
    notes: ''
  });

  const fetchMyBookings = useCallback(async () => {
    try {
      const response = await api.get('/api/consulting/my-bookings');
      setMyBookings(response.data);
    } catch (error) {
      // Bookings fetch failed
    }
  }, []);

  useEffect(() => {
    fetchMyBookings();
  }, [fetchMyBookings]);

  useEffect(() => {
    if (selectedDate) {
      const fetchSlots = async () => {
        try {
          const dateStr = format(selectedDate, 'yyyy-MM-dd');
          const response = await api.get(`/api/consulting/slots?date=${dateStr}`);
          setAvailableSlots(response.data.available_slots);
          setSelectedSlot(null);
        } catch (error) {
          // Slots fetch failed
        }
      };
      fetchSlots();
    }
  }, [selectedDate]);

  const handleBooking = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedSlot) {
      setError('Please select a date and time slot');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await api.post('/api/consulting/book', {
        date: format(selectedDate, 'yyyy-MM-dd'), time_slot: selectedSlot, ...formData
      });
      setBookingSuccess(true);
      setSelectedDate(null);
      setSelectedSlot(null);
      setFormData({ ...formData, topic: '', notes: '' });
      fetchMyBookings();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to book session');
    } finally {
      setLoading(false);
    }
  };

  const disabledDays = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today || date.getDay() === 0 || date.getDay() === 6;
  };

  return (
    <div className="p-8" data-testid="consulting-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Book Consulting Session
        </h1>
        <p className="text-[#71717A]">Schedule a 1-on-1 session with our trading experts</p>
      </div>

      {bookingSuccess && (
        <div className="bg-[#00FF66]/10 border border-[#00FF66]/30 rounded-md p-4 mb-6 flex items-center gap-3">
          <Check className="w-5 h-5 text-[#00FF66]" />
          <p className="text-[#00FF66]">Your session has been booked successfully!</p>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="bg-[#141417] border border-[#27272A] rounded-md p-6">
          <h2 className="text-xl font-bold text-white mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Select Date & Time
          </h2>
          
          <div className="mb-6">
            <CalendarComponent
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              disabled={disabledDays}
              className="rounded-md border border-[#27272A] bg-[#09090B] p-3"
              classNames={{
                day_selected: "bg-[#007AFF] text-white hover:bg-[#007AFF]",
                day_today: "bg-[#27272A] text-white",
              }}
            />
          </div>

          {selectedDate && (
            <div className="mb-6">
              <Label className="text-[#A1A1AA] mb-3 block">Available Time Slots</Label>
              {availableSlots.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {availableSlots.map(slot => (
                    <Button
                      key={slot}
                      variant={selectedSlot === slot ? 'default' : 'outline'}
                      className={selectedSlot === slot ? 'btn-primary' : 'border-[#27272A] text-[#A1A1AA]'}
                      onClick={() => setSelectedSlot(slot)}
                      data-testid={`slot-${slot}`}
                    >
                      <Clock className="w-4 h-4 mr-2" /> {slot}
                    </Button>
                  ))}
                </div>
              ) : (
                <p className="text-[#71717A] text-sm">No available slots for this date</p>
              )}
            </div>
          )}

          {error && (
            <div className="bg-[#FF3333]/10 border border-[#FF3333]/30 rounded-md p-4 mb-6 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-[#FF3333]" />
              <p className="text-[#FF3333]">{error}</p>
            </div>
          )}

          <form onSubmit={handleBooking} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#A1A1AA]">Name</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="input-dark mt-2" required data-testid="booking-name-input" />
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Email</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="input-dark mt-2" required data-testid="booking-email-input" />
              </div>
            </div>
            <div>
              <Label className="text-[#A1A1AA]">Phone (optional)</Label>
              <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="input-dark mt-2" data-testid="booking-phone-input" />
            </div>
            <div>
              <Label className="text-[#A1A1AA]">Topic</Label>
              <Input value={formData.topic} onChange={(e) => setFormData({ ...formData, topic: e.target.value })} placeholder="What would you like to discuss?" className="input-dark mt-2" required data-testid="booking-topic-input" />
            </div>
            <div>
              <Label className="text-[#A1A1AA]">Additional Notes</Label>
              <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Any specific questions or topics..." className="input-dark mt-2 min-h-[100px]" data-testid="booking-notes-input" />
            </div>
            <Button type="submit" className="btn-primary w-full" disabled={loading || !selectedDate || !selectedSlot} data-testid="book-session-submit-btn">
              {loading ? 'Booking...' : 'Book Session'}
            </Button>
          </form>
        </div>

        <BookingsList bookings={myBookings} />
      </div>
    </div>
  );
}
